import { useCallback, useContext, useMemo } from "react"
import { useAppDispatch, useAppSelector, useAppStore } from "@/reducers/hooks"
import { type Message as MessageType } from "@/reducers/messagesSlice"
import { selectActiveChat } from "@/reducers/chatsSliceSelectors"
import {
  ComposerSurface,
  setChatReplyMessage,
} from "@/reducers/chatRepliesSlice"
import { XmppContext } from "@/stream/xmppClient"
import XmppGroupChatApi from "@/api/xmppGroupChatApi"
import { sendGroupchatStanza } from "@/reducers/chatsSliceThunks"
import ChatHelpers from "@/lib/chatHelpers"
import XmppMessageRetractionApi from "@/api/xmppMessageRetractionApi"
import useUnlinkDocument from "@/hooks/useUnlinkDocument"
import useFindDeliveryOrder from "@/hooks/useFindDeliveryOrder"
import {
  allPinnedMessagesSelector,
  countPinnedMessagesFromChatCreator,
  isMessagePinned,
  oldestPinnedMessageFromChatCreator,
} from "@/reducers/pinnedSliceSelectors"
import {
  addPinnedMessage,
  removePinnedMessage,
} from "@/api/xeps/PinnedMessages"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import ClipboardWriter from "@/lib/clipboardWriter"

export const MAX_PINNED_MESSAGES_PER_CHAT = 3

export type PinDuration = "48h" | "30d"

const PIN_DURATION_TTL: Record<PinDuration, string> = {
  "48h": "172800",
  "30d": "2592000",
}

const useMessageActions = (
  message: MessageType,
  displayBody?: string,
  composerSurface: ComposerSurface = "driver",
) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const activeChat = useAppSelector(selectActiveChat)
  const { client, myJid } = useContext(XmppContext)
  const { chatInputRef } = useContext(ChatHistoryContext)

  const handleUnlinkDocument = useUnlinkDocument(message)
  const handleFindDeliveryOrder = useFindDeliveryOrder(message)

  const isPinned = useAppSelector(isMessagePinned(message))

  const allPinnedMessages = useAppSelector(allPinnedMessagesSelector)

  const countPinnedMessagesSelectorFromChat = useMemo(
    () => countPinnedMessagesFromChatCreator(activeChat.jid),
    [activeChat.jid],
  )

  const pinnedMessagesInChatCount = useAppSelector(
    countPinnedMessagesSelectorFromChat,
  )

  const oldestPinnedMessageSelector = useMemo(
    () => oldestPinnedMessageFromChatCreator(activeChat.jid),
    [activeChat.jid],
  )

  const oldestPinnedMessage = useAppSelector(oldestPinnedMessageSelector)

  const handlePinMessage = useCallback(
    async (duration: PinDuration) => {
      if (pinnedMessagesInChatCount === MAX_PINNED_MESSAGES_PER_CHAT) {
        if (oldestPinnedMessage) {
          await removePinnedMessage(client, oldestPinnedMessage.itemId)
        }
      }
      await addPinnedMessage(client, message, PIN_DURATION_TTL[duration])
    },
    [oldestPinnedMessage, pinnedMessagesInChatCount, client, message],
  )

  const handleUnpinMessage = useCallback(async () => {
    await removePinnedMessage(client, allPinnedMessages[message.id]?.itemId)
  }, [client, allPinnedMessages, message.id])

  const handlePin = !isPinned ? handlePinMessage : undefined

  const handleUnPin = isPinned ? handleUnpinMessage : undefined

  const canDelete =
    window.mucPlugin.canModerate(message, myJid) &&
    !ChatHelpers.isFromKeyContact(activeChat, message)

  // Prevent replying to missed calls messages
  // as they do not exist on the WhatsApp side
  const canBeRepliedTo = !message.missedCallNumber

  const handleReply = canBeRepliedTo
    ? () => {
        dispatch(
          setChatReplyMessage({
            chatJid: activeChat.jid,
            surface: composerSurface,
            message: message,
          }),
        )
        chatInputRef?.current?.focus()
      }
    : undefined

  const { enterForwardingMode } = useContext(ChatHistoryContext)

  const handleForward = () => {
    enterForwardingMode([message.id])
  }

  const handleDelete = canDelete
    ? async () => {
        await XmppMessageRetractionApi.retract(client, message, activeChat.jid)
        if (isPinned) {
          removePinnedMessage(
            client,
            allPinnedMessages[message.id]?.itemId,
          ).catch(() => {})
        }
      }
    : undefined

  const handleReact = async (emoji: string | null) => {
    const stanza = XmppGroupChatApi.buildReactionStanza(
      myJid.bare().toString(),
      activeChat.jid,
      message.id,
      emoji,
    )
    sendGroupchatStanza(client, dispatch, stanza, store.getState)
  }

  const handleCopy = async () => {
    ClipboardWriter.executeAndWriteResult(
      async () => displayBody ?? message.body ?? "",
    )
    navigator.vibrate?.(10)
  }

  return {
    handleReply,
    handleCopy,
    handleForward,
    handleDelete,
    handlePin,
    handleUnPin,
    handleReact,
    handleUnlinkDocument,
    handleFindDeliveryOrder,
  }
}

export default useMessageActions
